/**
 * RestrictionZonesV2Filter - Фильтры для слоя зон ограничений
 *
 * Позволяет включать/выключать отдельные типы зон:
 * - LIMITED (зоны катания)
 * - FORBIDDEN (зоны запрета катания)
 * - PERMITTED (зоны парковок)
 * - STOP_LIMIT (зоны запрета парковки)
 * - SPEED_LIMIT (ограничения скорости)
 *
 * А также показывать/скрывать фиктивные зоны и badge значки
 */

import { t } from '@lingui/macro';
import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import {
    onChangeRestrictionZonesV2Filter,
    onChangeShowFictitiousZones,
    onChangeShowTechnicalZones,
    onChangeShowInvisibleZones,
    onChangeShowRestrictionZonesV2Badges,
    onChangeShowFillZones,
    onChangeShowForbiddenExistingZones,
    onChangeShowForbiddenProposedZones,
    onChangeShowPermittedExistingZones,
    onChangeShowPermittedProposedZones,
} from '../../../ac/onChangeMapFilters';
import { RESTRICTION_ZONES_V2_FILTER } from '../../../constants/filterStyles';
import { getStyleFilterItems } from '../../../constants/filterStylesResolver';
import {
    RESTRICTION_ZONES_V2_RIDING_STYLE,
    RESTRICTION_ZONES_V2_PARKING_STYLE,
} from '../../../constants/layerStyles';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import LayerFeatureSearch from '../../LayerFeatureSearch/LayerFeatureSearch';
import { searchRestrictionZoneV2InTiles, highlightRestrictionZoneV2, clearRestrictionZoneV2Highlight } from '../../../utils/searchRestrictionZoneV2InTiles';
import styles from '../FilterSidebar.module.css';

const RestrictionZonesV2FiltersComponent = ({
    mapFilters,
    onChangeRestrictionZonesV2Filter,
    onChangeShowFictitiousZones,
    onChangeShowTechnicalZones,
    onChangeShowInvisibleZones,
    onChangeShowRestrictionZonesV2Badges,
    onChangeShowFillZones,
    onChangeShowForbiddenExistingZones,
    onChangeShowForbiddenProposedZones,
    onChangeShowPermittedExistingZones,
    onChangeShowPermittedProposedZones,
}) => {
    const filter = RESTRICTION_ZONES_V2_FILTER;
    let items = filter.items;
    if (filter.style && filter.styleKey) {
        items = getStyleFilterItems(filter.style, filter.styleKey);
    }
    const { reduxPath } = filter;
    // Цвета берём из единого источника — layerStyles, чтобы легенда всегда совпадала с картой
    const forbiddenStyle = RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1]; // FORBIDDEN
    const permittedStyle = RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0]; // PERMITTED
    // Функция для генерации SVG иконки зоны (все V2-зоны имеют заливку fill-opacity=0.3)
    const renderZoneIcon = (value, color, borderColor) => {
        const strokeColor = borderColor || color;
        const strokeWidth = value === 'SPEED_LIMIT' ? '1.5' : '2.5';

        return (
            <svg
                viewBox="0 0 32 24"
                aria-hidden="true"
                style={{ width: '20px', height: '15px' }}
            >
                <rect
                    x="2"
                    y="2"
                    width="28"
                    height="20"
                    fill={color}
                    fillOpacity="0.3"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    strokeOpacity="0.9"
                    rx="2"
                />
            </svg>
        );
    };
    const limitedStyle = RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0]; // LIMITED
    const stopLimitStyle = RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1]; // STOP_LIMIT
    const speedLimitStyle = items.find(({ value }) => value === 'SPEED_LIMIT') || { color: '#6d65b2', borderColor: '#4a4483' };

    return (
        <div>
            <div style={{ padding: '10px' }}>
                <LayerFeatureSearch
                    searchFn={searchRestrictionZoneV2InTiles}
                    highlightFn={highlightRestrictionZoneV2}
                    clearFn={clearRestrictionZoneV2Highlight}
                    placeholder={t`ID зоны`}
                    notFoundMessage={t`Зона ограничения не найдена в загруженных данных`}
                    successLabel={t`Найдена`}
                    searchLabel={t`Найти`}
                    resultFields={[ 'id', 'internal_id', 'type' ]}
                />
            </div>

            {/* ── Зоны ─────────────────────────────────────────── */}
            <div className={styles.legHead}>{t`Зоны`}:</div>
            <div className={classnames(styles.checkboxIconGrid, styles.checkboxIconGridLarge)}>

                {/* Катания (LIMITED) */}
                {renderZoneIcon('LIMITED', limitedStyle.color, limitedStyle.borderColor)}
                <label htmlFor="dev-LIMITED">{t`Катания`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="restrictionZonesV2"
                            id="dev-LIMITED"
                            checked={mapFilters[reduxPath].includes('LIMITED')}
                            value="LIMITED"
                            onChange={onChangeRestrictionZonesV2Filter}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Парковки — subheader + существующая / предлагаемая */}
                <span />
                <span style={{ fontSize: '13px', marginTop: '4px' }}>{t`Парковки`}</span>
                <span />
                {/* Sub-toggle: существующая */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill={permittedStyle.color}
                        fillOpacity="0.3"
                        stroke={permittedStyle.borderColor}
                        strokeWidth="2.5"
                        strokeOpacity="0.9"
                        rx="2"
                    />
                </svg>
                <label
                    htmlFor="dev-permitted-existing"
                    style={{ paddingLeft: '4px', whiteSpace: 'nowrap' }}
                >
                    {t`- существующая`}
                </label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="permittedExistingZones"
                            id="dev-permitted-existing"
                            checked={mapFilters.showPermittedExistingZones}
                            onChange={onChangeShowPermittedExistingZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
                {/* Sub-toggle: предлагаемая */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill={permittedStyle.proposedColor}
                        fillOpacity="0.15"
                        stroke={permittedStyle.proposedBorderColor}
                        strokeWidth="2.5"
                        strokeOpacity="0.9"
                        rx="2"
                    />
                </svg>
                <label
                    htmlFor="dev-permitted-proposed"
                    style={{ paddingLeft: '4px', whiteSpace: 'nowrap' }}
                >
                    {t`- предлагаемая`}
                </label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="permittedProposedZones"
                            id="dev-permitted-proposed"
                            checked={mapFilters.showPermittedProposedZones}
                            onChange={onChangeShowPermittedProposedZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            </div>

            {/* ── Зоны ограничения ─────────────────────────────── */}
            <div className={styles.legHead}>{t`Зоны ограничения`}:</div>
            <div className={classnames(styles.checkboxIconGrid, styles.checkboxIconGridLarge)}>

                {/* Запрета катания — subheader + существующая / предлагаемая */}
                <span />
                <span style={{ fontSize: '13px', marginTop: '4px' }}>{t`Запрета катания`}</span>
                <span />
                {/* Sub-toggle: существующая */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill={forbiddenStyle.color}
                        fillOpacity="0.3"
                        stroke={forbiddenStyle.borderColor}
                        strokeWidth="2.5"
                        strokeOpacity="0.9"
                        rx="2"
                    />
                </svg>
                <label
                    htmlFor="dev-forbidden-existing"
                    style={{ paddingLeft: '4px', whiteSpace: 'nowrap' }}
                >
                    {t`- существующая`}
                </label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="forbiddenExistingZones"
                            id="dev-forbidden-existing"
                            checked={mapFilters.showForbiddenExistingZones}
                            onChange={onChangeShowForbiddenExistingZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
                {/* Sub-toggle: предлагаемая */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill={forbiddenStyle.proposedColor}
                        fillOpacity="0.15"
                        stroke={forbiddenStyle.proposedBorderColor}
                        strokeWidth="2.5"
                        strokeOpacity="0.9"
                        rx="2"
                    />
                </svg>
                <label
                    htmlFor="dev-forbidden-proposed"
                    style={{ paddingLeft: '4px', whiteSpace: 'nowrap' }}
                >
                    {t`- предлагаемая`}
                </label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="forbiddenProposedZones"
                            id="dev-forbidden-proposed"
                            checked={mapFilters.showForbiddenProposedZones}
                            onChange={onChangeShowForbiddenProposedZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Запрета парковки (STOP_LIMIT) */}
                {renderZoneIcon('STOP_LIMIT', stopLimitStyle.color, stopLimitStyle.borderColor)}
                <label htmlFor="dev-STOP_LIMIT">{t`Запрета парковки`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="restrictionZonesV2"
                            id="dev-STOP_LIMIT"
                            checked={mapFilters[reduxPath].includes('STOP_LIMIT')}
                            value="STOP_LIMIT"
                            onChange={onChangeRestrictionZonesV2Filter}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Ограничения скорости (SPEED_LIMIT) */}
                {renderZoneIcon('SPEED_LIMIT', speedLimitStyle.color, speedLimitStyle.borderColor)}
                <label htmlFor="dev-SPEED_LIMIT">{t`Ограничения скорости`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="restrictionZonesV2"
                            id="dev-SPEED_LIMIT"
                            checked={mapFilters[reduxPath].includes('SPEED_LIMIT')}
                            value="SPEED_LIMIT"
                            onChange={onChangeRestrictionZonesV2Filter}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            </div>

            {/* ── Фильтры ──────────────────────────────────────── */}
            <div className={styles.legHead}>{t`Фильтры`}:</div>
            <div className={classnames(styles.checkboxIconGrid, styles.checkboxIconGridLarge)}>

                {/* Заливка зон */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill="#aaaaaa"
                        fillOpacity="0.4"
                        stroke="none"
                        rx="2"
                    />
                </svg>
                <label htmlFor="fill-dev-zones">{t`Заливка зон`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="fillZones"
                            id="fill-dev-zones"
                            checked={mapFilters.showFillZones}
                            onChange={onChangeShowFillZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Фиктивные зоны: пунктирная обводка. Служебный (dev) слой —
                    не выводим в легенду при экспорте растра (data-export-omit
                    на всей тройке ячеек: значок + подпись + переключатель). */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                    data-export-omit
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill="#999999"
                        fillOpacity="0.3"
                        stroke="#999999"
                        strokeWidth="2"
                        strokeDasharray="3,2"
                        rx="2"
                    />
                </svg>
                <label
                    htmlFor="fictitious-dev-zones"
                    data-export-omit
                >{t`Фиктивные зоны`}</label>
                <div data-export-omit>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="fictitiousZones"
                            id="fictitious-dev-zones"
                            checked={mapFilters.showFictitiousZones}
                            onChange={onChangeShowFictitiousZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Невидимые зоны: X-штриховка. Служебный (dev) слой — не выводим
                    в легенду при экспорте растра (data-export-omit на всей тройке
                    ячеек: значок + подпись + переключатель). */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                    data-export-omit
                >
                    <defs>
                        <pattern
                            id="crosshatch-invisible-legend"
                            x="0"
                            y="0"
                            width="10"
                            height="10"
                            patternUnits="userSpaceOnUse"
                        >
                            <line
                                x1="0"
                                y1="0"
                                x2="10"
                                y2="10"
                                stroke="#aaaaaa"
                                strokeWidth="1.5"
                                strokeOpacity="0.7"
                            />
                            <line
                                x1="10"
                                y1="0"
                                x2="0"
                                y2="10"
                                stroke="#aaaaaa"
                                strokeWidth="1.5"
                                strokeOpacity="0.7"
                            />
                        </pattern>
                    </defs>
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill="url(#crosshatch-invisible-legend)"
                        stroke="none"
                        rx="2"
                    />
                </svg>
                <label
                    htmlFor="invisible-dev-zones"
                    data-export-omit
                >{t`Невидимые зоны`}</label>
                <div data-export-omit>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="invisibleZones"
                            id="invisible-dev-zones"
                            checked={mapFilters.showInvisibleZones}
                            onChange={onChangeShowInvisibleZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Технические зоны: обводка без заливки + буква T */}
                <svg
                    viewBox="0 0 32 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '15px' }}
                >
                    <rect
                        x="2"
                        y="2"
                        width="28"
                        height="20"
                        fill="none"
                        stroke="#aaaaaa"
                        strokeWidth="2"
                        rx="2"
                    />
                    <text
                        x="16"
                        y="16"
                        textAnchor="middle"
                        fill="#aaaaaa"
                        fontSize="12"
                        fontWeight="bold"
                        fontFamily="sans-serif"
                    >T</text>
                </svg>
                <label htmlFor="technical-dev-zones">{t`Гео вылеты`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="technicalZones"
                            id="technical-dev-zones"
                            checked={mapFilters.showTechnicalZones}
                            onChange={onChangeShowTechnicalZones}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>

                {/* Значки зон: щит белым контуром */}
                <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    style={{ width: '20px', height: '20px' }}
                >
                    <path
                        d="M12,23 L20,18 L20,5 L4,5 L4,18 Z"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                    />
                </svg>
                <label htmlFor="badge-dev-zones">{t`Значки зон`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name="badgeZones"
                            id="badge-dev-zones"
                            checked={mapFilters.showRestrictionZonesV2Badges}
                            onChange={onChangeShowRestrictionZonesV2Badges}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            </div>
        </div>
    );
};
const mapStateToProps = ({ mapFilters }) => ({ mapFilters });
const ConnectedComponent = connect(mapStateToProps, { onChangeRestrictionZonesV2Filter, onChangeShowFictitiousZones, onChangeShowTechnicalZones, onChangeShowInvisibleZones, onChangeShowRestrictionZonesV2Badges, onChangeShowFillZones, onChangeShowForbiddenExistingZones, onChangeShowForbiddenProposedZones, onChangeShowPermittedExistingZones, onChangeShowPermittedProposedZones })(RestrictionZonesV2FiltersComponent);

export const RestrictionZonesV2Legend = withDisplayedLayersCheck(ConnectedComponent, t`Зонирование`);
